// Crops database — content sourced from https://www.worldfarmers.org/crops_recipes/
// (descriptions, scientific names, origins, and varieties are faithful to the
// source, lightly copy-edited). Photos are the real crop photos from that page,
// stored locally in /images/wf/crops/. Per the client, recipe links from the old
// site are NOT included — recipes will be uploaded as PDFs later; `recipeCount`
// records how many recipes the old page listed so cards can show a "recipes
// coming soon" note. `category`, `region`, and `cultural` are editorial
// groupings added for filtering.

export const CROP_CATEGORIES = ["Leafy greens", "Herbs", "Fruiting vegetables", "Squash & gourds", "Roots & alliums", "Beans & peas", "Brassicas", "Grains & staples"];

export const CROP_REGIONS = ["Africa", "Asia", "Americas", "Europe & Mediterranean"];

export const CROPS = [
  {
    "id": "african-corn",
    "name": "African Corn",
    "aka": null,
    "scientificName": "Zea mays subsp. mays",
    "origin": "East Africa",
    "region": "Africa",
    "category": "Grains & staples",
    "cultural": true,
    "description": "African corn, also known as maize, has much tougher kernels than that of corn found in the United States. It was introduced to Africa in the 1500s and has been a dominant crop since. Maize can be cooked or ground into cornmeal for use in a variety of dishes around the world. Corn dishes are a central staple in Eastern African cooking, served as the starch staple of a meal. Although this crop is rich in vitamins A, C, and E, if it is depended on highly in one’s diet, it can lead to malnutrition.",
    "varieties": [],
    "images": [
      "/images/wf/crops/african-corn-1.jpg",
      "/images/wf/crops/african-corn-2.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "amaranth",
    "name": "Amaranth",
    "aka": null,
    "scientificName": "Amaranthus spp.",
    "origin": "Mediterranean, East Africa",
    "region": "Africa",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Amaranth can have an all-green leaf, all-red leaf, or one that is lightly striped with red. It may also be known as Calaloo or Mchicha. Amaranth grain has become popular in the United States in recent years, but for East African farmers, the best part of the plant is the leaves, which are high in vitamin A, vitamin C, and calcium. Amaranth can be prepared in the same ways as cooked spinach or kale.",
    "varieties": [],
    "images": [
      "/images/wf/crops/amaranth-1.jpg",
      "/images/wf/crops/amaranth-2.jpg",
      "/images/wf/crops/amaranth-3.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "asian-arugula",
    "name": "Asian Arugula",
    "aka": null,
    "scientificName": "Eruca sativa",
    "origin": "Mediterranean",
    "region": "Europe & Mediterranean",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Asian arugula is an herb that is native to the Mediterranean region. It has a peppery flavor. Asian arugula can be steamed, blanched, or served raw. The leaves are best used when they have grown to be 2-3 inches long, for the older they get, the spicier they become. The flowers of arugula are aslo edible and have a similar taste as the leaves. The seeds yield an edible oil that, if stored for six months, can be used as a substitute for rapeseed oil.",
    "varieties": [],
    "images": [
      "/images/wf/crops/asian-arugula-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "asian-cucumber",
    "name": "Asian Cucumber",
    "aka": null,
    "scientificName": "Trichosanthes kirilowii",
    "origin": "Mediterranean",
    "region": "Europe & Mediterranean",
    "category": "Fruiting vegetables",
    "cultural": true,
    "description": "Asian Cucumbers are crossed with different types of melons, making them slightly sweeter and often shorter and thicker than the Western varieties. They can be used for slicing, salads, pickling, etc. Large Asian cucumbers are also sometimes added to soups. Dressed in a tangy vinaigrette, these are the perfect accompaniment to any fried or grilled dish. When small, Asian cucumbers are eaten like other cumbers - raw or pickled - but when they fully grown, Asian cucumbers take on more of a sweet melon quality.",
    "varieties": [],
    "images": [
      "/images/wf/crops/asian-cucumber-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "asian-mustards",
    "name": "Asian Mustards",
    "aka": null,
    "scientificName": "Brassica juncea",
    "origin": "India",
    "region": "Asia",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Asian mustard varieties can fall into two categories: a leafy, kale-like varietal, or a cabbagelike head. The colors can range from green, which is the most common, to red or purple. In China and Japan, Asian mustard is used in soups, salads, and stir-fries. The seeds can be used whole in curries and pickles. In Korea, the seeds are also used in the treatment of colds and stomach disorders. The Chinese also add the leaves to soup for bladder inflamation.",
    "varieties": [
      {
        "name": "Asian Mustard (curled)",
        "scientificName": "Brassica juncea",
        "origin": "India",
        "description": "Curly Asian mustard has a peppery flavor on the leaves than can range from mild to hot. The seeds can be used to make brown mustard."
      },
      {
        "name": "Asian Mustard (green)",
        "scientificName": "Brassica juncea",
        "origin": "India",
        "description": "Green Asian mustard is spicy and hot. It is slightly pungent when when cooked."
      },
      {
        "name": "Asian Mustard (red)",
        "scientificName": "Brassica juncea",
        "origin": "Asia",
        "description": "Red Asian mustard originally comes from Japan. It has a mild, spicy flavor. This crop is harvested young for salads, braising mixes, and garnish."
      }
    ],
    "images": [
      "/images/wf/crops/asian-mustards-1.jpg",
      "/images/wf/crops/asian-mustards-2.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "basil",
    "name": "Basil",
    "aka": null,
    "scientificName": "Ocimum basilicum",
    "origin": "Asia",
    "region": "Asia",
    "category": "Herbs",
    "cultural": false,
    "description": "Basil, an herb belonging to the mint family, is native to India and China. It is most commonly used to make pesto or season vegetables and fish. The diversity of flavor in basil allows for it to be used in many diverse ways. Cosmetic companies use basil oil in soaps, perfumes, and lotions. We grow many different kinds of basil.",
    "varieties": [
      {
        "name": "Lemon Basil",
        "scientificName": "Ocimum × citriodorumis",
        "origin": "India",
        "description": "Lemon Basil is light green with white flowers. It has a citrus taste and flavor to it, so its recommended use is as a garnish on a drink, an addition to a salad, or an accompaniment to fish. Lemon basil seeds are also a key ingredient in Asian sweets."
      },
      {
        "name": "Purple Basil",
        "scientificName": "Ocimum basilicum var purpurescens",
        "origin": "India",
        "description": "Purple basil has a dark purple color with ruffled leaves. It makes an excellent addition to salads and can also make a unique, dark purple pesto."
      },
      {
        "name": "Sweet Italian Basil",
        "scientificName": "Ocimum basilicum",
        "origin": "Asia",
        "description": "Sweet Italian basil is the basil variety most familiar to Americans. It is frequently used in Italian sauces, soups, and pesto, but is also a wonderful addition to stir fries, noodle soups, and salads."
      },
      {
        "name": "Thai Basil",
        "scientificName": "Ocimum basilicum var. thyrsiflora",
        "origin": "Asia",
        "description": "Thai Basil has bright green leaves with purple blossoms and stems. It has a spicy taste lightly reminiscent of star anise or cloves taste. It is excellent in Thai-style soups or pho."
      }
    ],
    "images": [
      "/images/wf/crops/basil-1.jpg",
      "/images/wf/crops/basil-2.jpg",
      "/images/wf/crops/basil-3.jpg"
    ],
    "recipeCount": 7
  },
  {
    "id": "beets",
    "name": "Beets",
    "aka": null,
    "scientificName": "Beta vulgaris",
    "origin": "Europe",
    "region": "Europe & Mediterranean",
    "category": "Roots & alliums",
    "cultural": false,
    "description": "Beets are a root vegetable that originates in the Mediterranean. Different types of beets have different looks and tastes: classic beetroot is purple and has a sweet, earthy taste. The golden beet appears bright orange, but the flesh is yellow and has a mild, buttery taste. Chioggia beet are a deep purple, but the flesh has pink and white rings and has a very mild taste. All varieties can be served raw in salads, roasted, or boiled.",
    "varieties": [],
    "images": [
      "/images/wf/crops/beets-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "bitter-melon",
    "name": "Bitter Melon",
    "aka": null,
    "scientificName": "Momordica charantia",
    "origin": "India",
    "region": "Asia",
    "category": "Fruiting vegetables",
    "cultural": true,
    "description": "Bitter melon are a long, slender crop in the Cucurbitaceae family. The skin is rough and dimpled, changing from a green color to orange when fully matured. The inside is white and crisp. It is recommended to harvest while the fruit is young and green, for when it turns orange, it is very bitter. Bitter melon soup is a popular dish in East Asia, but they can also be stir-fried, braised, or pickled.",
    "varieties": [],
    "images": [
      "/images/wf/crops/bitter-melon-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "bok-choy",
    "name": "Bok Choy",
    "aka": null,
    "scientificName": "Brassica rapa, sub. chinensis",
    "origin": "China",
    "region": "Asia",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Bok Choy is made up of elongated leafy stalks that radiate out from a bulbous central stem, similar to a bunch or head of celery, and about the same length (although a full-size bok choy has a wider diameter than a bunch of celery). The leaves are dark green, sometimes ruffly and sometimes flat, and the stems are either white or a lighter green, depending on the variety. Sometimes bok choy is harvested when it's immature and sold as baby bok choy. Baby bok choy can range from 3 to 6 inches in length. It tends to be a little bit sweeter and can be chopped like larger bok choy, separated into leaves, or cooked whole. Along with being crunchy and delicious, bok choy is full of fiber, vitamins, minerals, and other nutrients that make it a beneficial addition to your diet. Like other dark, leafy greens, it’s full of antioxidants and other compounds that help to promote better health.",
    "varieties": [],
    "images": [
      "/images/wf/crops/bok-choy-1.jpg",
      "/images/wf/crops/bok-choy-2.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "broccoli",
    "name": "Broccoli",
    "aka": null,
    "scientificName": "Brassica oleracea",
    "origin": "Mediterranean",
    "region": "Europe & Mediterranean",
    "category": "Brassicas",
    "cultural": false,
    "description": "Broccoli belongs to the cabbage family. It is an easy-to-grow crop that grows well in mild environments. Broccoli is a low-calorie food that is high in vitamins C and K. Broccoli was first introduced to the United States by Southern Italian immigrants, but did not become widely popular until the 1920s.",
    "varieties": [],
    "images": [
      "/images/wf/crops/broccoli-1.jpg"
    ],
    "recipeCount": 3
  },
  {
    "id": "cabbage",
    "name": "Cabbage",
    "aka": null,
    "scientificName": "Brassica oleracea var. capitata",
    "origin": "Europe",
    "region": "Europe & Mediterranean",
    "category": "Brassicas",
    "cultural": false,
    "description": "Cabbage comes in a variety of shapes and colors, including red, purple, white, and green, and its leaves can be either crinkled or smooth. Head of cabbage, which can grow from 0.5 to 4 kilograms, is rich in vitamins and minerals, has almost no fat and is very rich in fiber which makes it very healthy to eat. Cabbage is highly nutritious and rich in vitamin C, fiber, and vitamin K. Some research suggests that it may support digestion, improve heart health, and decrease inflammation.",
    "varieties": [],
    "images": [
      "/images/wf/crops/cabbage-1.jpg",
      "/images/wf/crops/cabbage-2.jpg",
      "/images/wf/crops/cabbage-3.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "carrots",
    "name": "Carrots",
    "aka": null,
    "scientificName": "Daucus carota",
    "origin": "Middle East",
    "region": "Europe & Mediterranean",
    "category": "Roots & alliums",
    "cultural": false,
    "description": "Carrots are a root vegetable that are thought to have originated in what is now Afghanistan. The leaves are rich in vitamin E and the root is rich in carotene, which is converted into vitamin A by the liver. The juice can be extracted as a healthy drink, the seeds are useful in medical treatment, and the greens can be sauteed until crisp to make a beautiful, slightly bitter garnish.",
    "varieties": [],
    "images": [
      "/images/wf/crops/carrots-1.jpg",
      "/images/wf/crops/carrots-2.jpg"
    ],
    "recipeCount": 3
  },
  {
    "id": "celery",
    "name": "Celery",
    "aka": null,
    "scientificName": "Apium graveolens var. dulce",
    "origin": "Europe",
    "region": "Europe & Mediterranean",
    "category": "Fruiting vegetables",
    "cultural": false,
    "description": "Celery can be a healthful part of your diet. It contains anti-inflammatory and antioxidant compounds and may support digestion. Celery has long, firm, pale green fibrous stalks and grows in bunches of approximately eight to 10. The stalks taper into leaves at the top. Although most people discard the leaves, they are also edible. Celery has a mild, earthy, slightly peppery taste. At just 10 calories a stalk, celery’s claim to fame may be that it’s long been considered a low-calorie “diet food.” You can eat it raw or cooked, and it makes a great addition to smoothies, stir-fries, soups, and juices. Celery can also be steamed or baked.",
    "varieties": [],
    "images": [
      "/images/wf/crops/celery-1.jpg"
    ],
    "recipeCount": 3
  },
  {
    "id": "chinese-broccoli",
    "name": "Chinese Broccoli",
    "aka": null,
    "scientificName": "Brassica oleracea var. alboglabras",
    "origin": "China",
    "region": "Asia",
    "category": "Brassicas",
    "cultural": true,
    "description": "Also known as gai lan, Chinese broccoli has thick, flat leaves and thick stems with small flower heads that resemble miniature broccolis. Its flavor is similar to broccoli, although slightly more bitter. Common preparations are in stir-fries or boiled and served with a little soy or oyster sauce.",
    "varieties": [],
    "images": [
      "/images/wf/crops/chinese-broccoli-1.jpg",
      "/images/wf/crops/chinese-broccoli-2.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "chinsaga",
    "name": "Chinsaga",
    "aka": null,
    "scientificName": "Cleome gynandra",
    "origin": "East Africa",
    "region": "Africa",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Chinsaga, a crop that is very tolerant of intense sun, heat, and drought conditions, is indigenous to Africa. It contains high levels of of vitamin C and beta-carotene, along with iron, calcium and magnesium. Spider plant can be stewed in water or milk, and is often made with whole or powdered groundnuts (peanuts). The leaves, flowers, pods, and tender stems are highly nutritious and are used in traditional medicine to help nursing mothers.",
    "varieties": [],
    "images": [
      "/images/wf/crops/chinsaga-1.jpg",
      "/images/wf/crops/chinsaga-2.jpg",
      "/images/wf/crops/chinsaga-3.jpg"
    ],
    "recipeCount": 3
  },
  {
    "id": "chipili-n",
    "name": "Chipilín",
    "aka": null,
    "scientificName": "Crotalaria longirostrata",
    "origin": "Central America",
    "region": "Americas",
    "category": "Fruiting vegetables",
    "cultural": false,
    "description": "Chipilin is popular in the cuisines of El Salvador and southern Mexico, where it is most frequently used in tamales and soups. Although chipilin is prepared like an herb, it is technically a legume, and is thus much higher in protein than similar leafy greens. Chipilin leaves must be cooked in order to be safe to eat.",
    "varieties": [],
    "images": [
      "/images/wf/crops/chipili-n-1.jpg",
      "/images/wf/crops/chipili-n-2.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "chives",
    "name": "Chives",
    "aka": null,
    "scientificName": "Allium schoenoprasum um",
    "origin": "China",
    "region": "Asia",
    "category": "Herbs",
    "cultural": false,
    "description": "Chives, also known as wild chives or flowering onion, are mostly grown in Europe and North America. The greens are an excellent addition to mixed salads and soups because of their mild onion flavor. The bulbs are small but they can be harvested with the leaves attached and used in the same way as spring onions. Chives have similar medicinal qualities to garlic, but in a milder form. They are also used as ornamentals because of their attractive violet flowers.",
    "varieties": [],
    "images": [
      "/images/wf/crops/chives-1.jpg",
      "/images/wf/crops/chives-2.jpg"
    ],
    "recipeCount": 3
  },
  {
    "id": "chrysanthemum",
    "name": "Chrysanthemum",
    "aka": null,
    "scientificName": "Chrysanthemum coronarium",
    "origin": "Northern Europe, East Asia",
    "region": "Asia",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Chrysanthemums are flowering plants native to Asia and Northeastern Europe. The flowers can be boiled to make a fragrant tea, while the leaves are often steamed or boiled in Chinese cuisine. Of course, the entire flower can be used as a garnish or table decoration.",
    "varieties": [],
    "images": [
      "/images/wf/crops/chrysanthemum-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "cilantro",
    "name": "Cilantro",
    "aka": null,
    "scientificName": "Coriandrum sativum",
    "origin": "Mediterranean",
    "region": "Europe & Mediterranean",
    "category": "Herbs",
    "cultural": false,
    "description": "Cilantro is an annual herb. All parts of the plant are edible, including the seeds: when dried, the seeds are called coriander. Fresh cilantro leaves are used in dishes from all over the world, from Central American guacamole to Thai noodle soups and Indian curries. Fresh cilantro has a pleasant citrusy flavor.",
    "varieties": [],
    "images": [
      "/images/wf/crops/cilantro-1.jpg",
      "/images/wf/crops/cilantro-2.jpg"
    ],
    "recipeCount": 3
  },
  {
    "id": "collard-greens",
    "name": "Collard Greens",
    "aka": null,
    "scientificName": "Brassica oleracea",
    "origin": "Europe",
    "region": "Europe & Mediterranean",
    "category": "Leafy greens",
    "cultural": false,
    "description": "Collard greens are a winter-hardy crop originating in Southeast Asia. Collard greens are high in many nutrients and are rich in vitamin A. Collard greens have a strong cabbage flavor, though late-season collards become sweeter as a result of frost. Collard greens can be eaten raw, sauteed, stir-fried, or added to soup or stews; they are a common ingredient in many world cuisines, from classics of the American South to staple dishes of East Africa.",
    "varieties": [],
    "images": [
      "/images/wf/crops/collard-greens-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "cowpea-leaves-kunde",
    "name": "Kunde",
    "aka": [
      "Cowpea leaves"
    ],
    "scientificName": "Vigna unguiculata (L.) Walp.",
    "origin": "Africa",
    "region": "Africa",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Cowpea (also known as a black-eyed pea) is a climbing annual grown mainly for its edible beans, In countries such as Kenya, Southeast Asia and other African countries, cowpeas farming has become more prevalent. Cowpea is an important grain and seed legume in Africa, parts of the Americas and in Asia. In addition to their use as a protein-rich food crop, cowpeas are extensively grown as a hay crop and as a green manure or cover crop. In Kenya, cowpeas leaves are known as kunde. They have been used throughout time, up to the present-day, on the African continent as a staple good in nutritious traditional foods.  Its leaves and seed are consumed to meet the dietary requirements of protein and micronutrient in rural African communities. Their tender leaves and the immature seeds and pods are often served boiled or fried and are used to make delicious dishes. Cowpea leaves have many health benefits. They contain important nutrients including vitamins, minerals and are rich in micronutrients, nutraceuticals, and antioxidants.",
    "varieties": [],
    "images": [
      "/images/wf/crops/cowpea-leaves-kunde-1.jpg",
      "/images/wf/crops/cowpea-leaves-kunde-2.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "cranberry-beans",
    "name": "Cranberry Beans",
    "aka": null,
    "scientificName": "Phaseolus vulgaris L. cv. SVM Taylor",
    "origin": "South America",
    "region": "Americas",
    "category": "Beans & peas",
    "cultural": true,
    "description": "Fresh cranberry beans can be identified by their distinctive pods, which are streaked with cream and magenta. Fresh beans must be used quickly or they will begin to mold; storing them in a paper bag in the fridge will keep them good for 2-3 days. Fresh beans also freeze well, even without being blanched first. Cranberry beans are typically prepared simply: just boil until they are tender but not mushy, then toss with olive oil and salt.",
    "varieties": [],
    "images": [
      "/images/wf/crops/cranberry-beans-1.jpg",
      "/images/wf/crops/cranberry-beans-2.jpg",
      "/images/wf/crops/cranberry-beans-3.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "cucumber",
    "name": "Cucumber",
    "aka": null,
    "scientificName": "Cucumis sativus",
    "origin": "Southeast Asia",
    "region": "Asia",
    "category": "Fruiting vegetables",
    "cultural": false,
    "description": "Cucumbers originated in India and were brought to the Americas during European colonization. Cucumbers are used for various purposes around the world. The Chinese cut the overripe fruits into soups. Indian cuisine employs cucumbers in its yogurt-based condiments. In Japan, they use them for soup. In Korea they use them for stir-fry. Asian cucumbers have textured skin and a smaller seed cavity. The juice and fruit are used in many skin care and beauty products.",
    "varieties": [],
    "images": [
      "/images/wf/crops/cucumber-1.jpg"
    ],
    "recipeCount": 3
  },
  {
    "id": "daikon-radish",
    "name": "Daikon Radish",
    "aka": null,
    "scientificName": "Raphanus sativus",
    "origin": "Southeast Asia",
    "region": "Asia",
    "category": "Roots & alliums",
    "cultural": true,
    "description": "Daikon is a mild-flavored winter radish that has a large, very white root. Daikon is excellent pickled, and can also be grated and mixed together with soy sauce and rice vinegar to make ponzu, a popular condiment in Japanese cuisine. Because of its mild taste and firm texture, daikon can also be used in place of potatoes in many contexts, including roasting, hashbrowns, and in soups. It is low in calories and especially high in vitamin C.",
    "varieties": [],
    "images": [
      "/images/wf/crops/daikon-radish-1.jpg",
      "/images/wf/crops/daikon-radish-2.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "dill",
    "name": "Dill",
    "aka": null,
    "scientificName": "Anethum graveolens",
    "origin": "Mediterranean",
    "region": "Europe & Mediterranean",
    "category": "Herbs",
    "cultural": false,
    "description": "Fresh dill leaves are a common ingredient across Europe, the Middle East, the Indian subcontinent, and East Asia. In Scandinavia, dill is a favorite addition to salmon and other fish dishes; in Russia, dill is a very popular addition to borscht, boiled potatoes, and sour cream; in parts of Thailand, it is added to papaya salad. In the United States, one of dill's most popular uses is as a flavoring to the beloved dill pickle.",
    "varieties": [],
    "images": [
      "/images/wf/crops/dill-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "edible-luffa",
    "name": "Edible Luffa",
    "aka": null,
    "scientificName": "Luffa acutangula",
    "origin": "India",
    "region": "Asia",
    "category": "Squash & gourds",
    "cultural": true,
    "description": "While the word luffa (or loofah) may evoke images of a bath sponge, the edible variety is actually a member of the cucumber family and is cultivated and eaten as a vegetable. Luffa must be harvested young in order to be edible; as it ages, it becomes fibrous, at which point it becomes the source of the loofah sponge. Edible luffa is most popular in China and Vietnam, where it is commonly added to stir-fries and soups.",
    "varieties": [],
    "images": [
      "/images/wf/crops/edible-luffa-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "eggplant",
    "name": "Eggplant",
    "aka": null,
    "scientificName": "Solanum melongena",
    "origin": "Southeast Asia",
    "region": "Asia",
    "category": "Fruiting vegetables",
    "cultural": false,
    "description": "Eggplants are originally from what is now India, thougn now they have spread out throughout the world. They arrived in the New World with European colonization. There are many varieties of eggplant, from pure white to striped to the classic dark purple or near-black. Eggplants can be used as a meat substitute, used in tempura, fried, braised or stuffed. They are a very low-calorie food.",
    "varieties": [],
    "images": [
      "/images/wf/crops/eggplant-1.jpg",
      "/images/wf/crops/eggplant-2.jpg"
    ],
    "recipeCount": 3
  },
  {
    "id": "finger-millet",
    "name": "Finger Millet",
    "aka": null,
    "scientificName": "Eleusine coracana",
    "origin": "Eastern Africa",
    "region": "Africa",
    "category": "Grains & staples",
    "cultural": true,
    "description": "Millet is a grain and a staple food in many parts of the world. There are many different kinds of millet, which all have similar health benefits. Finger millet is gaining popularity worldwide because of how easy it is to grow and how adaptable it is as a food. It’s used to make bread, beer, and cereal. Today, finger millet can be found in health food stores and large supermarkets throughout the US, and it’s widely used as an alternative to wheat or other grains. Millet can be found in a variety of different forms. Dried millet, ground millet, and puffed millet are all common styles.",
    "varieties": [],
    "images": [
      "/images/wf/crops/finger-millet-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "fuzzy-melon-fuzzy-gourd",
    "name": "Fuzzy Melon",
    "aka": [
      "Fuzzy Gourd"
    ],
    "scientificName": "Benincasa hispida var. chieh-gua",
    "origin": "China",
    "region": "Asia",
    "category": "Squash & gourds",
    "cultural": true,
    "description": "The winter melon, also called white gourd, ash gourd, or “fuzzy melon”, is a vine grown for its very large fruit, eaten as a vegetable when mature. It is the only member of the genus Benincasa. The fruit is fuzzy when young. The immature melon has thick white flesh that is sweet when eaten. By maturity, the fruit loses its hairs and develops a waxy coating, giving rise to the name wax gourd, and providing a long shelf life. The melon may grow as large as 80cm in length. Although the fruit is referred to as a “melon,” the fully grown crop is not sweet. Originally cultivated in Southeast Asia, the winter melon is now widely grown in East Asia and South Asia as well. Fuzzy Melon with Dried Scallops (节瓜干贝 Mao Gua Gan Bei)",
    "varieties": [],
    "images": [
      "/images/wf/crops/fuzzy-melon-fuzzy-gourd-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "garden-eggs",
    "name": "Garden Eggs",
    "aka": null,
    "scientificName": "Solanum aethiopicum",
    "origin": "Africa",
    "region": "Africa",
    "category": "Fruiting vegetables",
    "cultural": true,
    "description": "Garden eggs, also known as African eggplants, are a type of vegetable that is native to Africa. They are part of the nightshade family and are a staple in many African diets. They can be white, green, yellow, or red, and it has a smooth texture and a slightly sweet flavor. Garden eggs are typically eaten cooked, but they can also be eaten raw. Garden eggs can be used in many different recipes, including stews, soups, salads, and curries. They can also be roasted, grilled, or fried. Garden eggs have been shown to improve blood sugar control, and they can also help to lower cholesterol levels. Additionally, garden eggs are a good source of dietary fibre, which can help to promote gut health. Garden eggs are also low in calories and fat, making them a healthy addition to any diet.",
    "varieties": [],
    "images": [
      "/images/wf/crops/garden-eggs-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "garlic",
    "name": "Garlic",
    "aka": null,
    "scientificName": "Allium sativum",
    "origin": "Africa",
    "region": "Africa",
    "category": "Roots & alliums",
    "cultural": false,
    "description": "Garlic is used widely as a flavoring in cooking, but it has also been used as a medicine throughout ancient and modern history; it has been taken to prevent and treat a wide range of conditions and diseases. In food, garlic is used in small quantities and contains very few calories, fat, protein, or carbohydrates. Its health action comes from the enzymes and unique compounds that it contains.",
    "varieties": [],
    "images": [
      "/images/wf/crops/garlic-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "garlic-scapes",
    "name": "Garlic Scapes",
    "aka": null,
    "scientificName": "Allium sativum",
    "origin": "Central Asia",
    "region": "Asia",
    "category": "Herbs",
    "cultural": false,
    "description": "A member of the allium family, garlic is related to onions, leeks, chives, and shallots. Garlic is native to central Asia and northeastern Iran. Across the world, garlic is used as a seasoning and as a common ingredient in traditional medicines. In New England, garlic is planted in the fall and overwinters under a layer of frozen soil. In the early spring, garlic scapes, a green tube with a distinctive curl and a flowerbud-shaped end, grow out of the tops of the garlic plants. Scapes have a milder flavor than clove garlic, but can be used in all the same ways, from sautés to roasts to pickling. Since most garlic in cultivation is reproduced asexually (by planting a clove), there is no need to allow the flowerbud at the end of the garlic scape to flower and go to seed. Harvesting the scapes is beneficial for the garlic plant, as it allows the plant to redirect energy back into growing the bulb.",
    "varieties": [],
    "images": [
      "/images/wf/crops/garlic-scapes-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "gongura-red-sorrel-or-jamaica-sorrel-roselle",
    "name": "Gongura",
    "aka": [
      "Red Sorrel",
      "Jamaica Sorrel",
      "Roselle"
    ],
    "scientificName": "Hibiscus sabdariffa",
    "origin": "India and Malaysia",
    "region": "Asia",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Gongura leaves may be pickled, steamed, blanched, or ground into a paste and combined with garlic, chilies, and salt to make chutney. The sour leaves heighten the rich flavor of legumes and fatty meats, therefore making them a perfect complement to dishes with lentils, goat, or mutton. Gongura leaves can be cooked with shrimp, mussels, and fish and are also used raw in salads.",
    "varieties": [],
    "images": [
      "/images/wf/crops/gongura-red-sorrel-or-jamaica-sorrel-roselle-1.jpg",
      "/images/wf/crops/gongura-red-sorrel-or-jamaica-sorrel-roselle-2.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "green-beans",
    "name": "Green Beans",
    "aka": null,
    "scientificName": "Phaseolus vulgaris",
    "origin": "Central/South America",
    "region": "Americas",
    "category": "Beans & peas",
    "cultural": false,
    "description": "Green beans, also known as pod beans, are categorized into two types: bush beans and pole beans. Bush beans grow in a bush that, if you stop harvesting, will stop producing. Pole beans, on the other hand, grow on a trellis and will continue to produce until the first frost. Green beans can be eaten raw, though they are more commonly steamed before serving.",
    "varieties": [],
    "images": [
      "/images/wf/crops/green-beans-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "hakurei-japanese-turnip",
    "name": "Hakurei (Japanese turnip)",
    "aka": null,
    "scientificName": "Brassica rapa",
    "origin": "Japan",
    "region": "Asia",
    "category": "Roots & alliums",
    "cultural": true,
    "description": "The Hakurei turnip, or Tokyo turnip is the member of the Brassica family. The Hakurei turnip is of Japanese variety, and is something referred to as a salad turnip, due to its crisp, delicious raw flavor. Hakurei turnips do not need to be cooked, you can eat them raw, or cook with their greens to enhance their natural sweetness. Turnips are one of the world’s oldest cultivated vegetables and they have a rich history. They were a well-established crop in Hellenistic and Roman times and later they were known as the “vegetable of nobility” in Europe. Hakurei are relatively new turnips that were developed in Japan in the ‘50s when the country was suffering from severe food shortages due to World War II. Their surprisingly delicate, almost fruity flavor and crunchy texture accounts for their popularity. They’re delicious raw, but you can also try shaving them into salads or slaws along with thinly sliced apples or pears. When cooked, they develop a buttery flavor and when roasted at high temperatures, their sweetness increases. Hakurei turnips are versatile enough to pair with just about anything, and they’re excellent in gratins, stir-fries, soups, or roasted with other roots vegetables. They can be braised, fried, glazed, or sautéed. Turnips are an excellent source of vitamin C, folic acid, and fiber, as well as thiamine, potassium, and vitamins B6 and E.",
    "varieties": [],
    "images": [
      "/images/wf/crops/hakurei-japanese-turnip-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "hot-asian-peppers",
    "name": "Hot Asian Peppers",
    "aka": null,
    "scientificName": "Capsicum ssp.",
    "origin": "Southeast Asia",
    "region": "Asia",
    "category": "Fruiting vegetables",
    "cultural": true,
    "description": "Hot Asian peppers include several varieties, all of which are quite hot. Some, like the chili and cayenne peppers, are often dried and ground into powder. In Thai and Indian cuisine, fresh Hot Asian peppers are used in curries. They are also used in Vietnamese sauces, Korean kimchi and Szechwan stir-fries and soups. These hot peppers have become ubiquitous across the globe and are adaptable for all spice needs.",
    "varieties": [],
    "images": [
      "/images/wf/crops/hot-asian-peppers-1.jpg",
      "/images/wf/crops/hot-asian-peppers-2.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "jilo",
    "name": "Jiló",
    "aka": null,
    "scientificName": "Solanum aethiopicum 'Gilo Group'",
    "origin": "Southeast Asia",
    "region": "Asia",
    "category": "Fruiting vegetables",
    "cultural": false,
    "description": "Jiló, also called a Scarlet Eggplant or a Garden Egg, is related to both tomatoes and eggplants. After being brought to Brazil from West Africa during the slave trade, jilo became extremely popular in Brazilian cuisine. With a slightly bitter taste, jilo is traditionally steamed or boiled until tender. However, it can also be prepared in the same way as other eggplants, from eggplant parmesan to ratatouille.",
    "varieties": [],
    "images": [
      "/images/wf/crops/jilo-1.jpg",
      "/images/wf/crops/jilo-2.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "kale",
    "name": "Kale",
    "aka": null,
    "scientificName": "Brassica oleracea var. sabellica",
    "origin": "Mediterranean",
    "region": "Europe & Mediterranean",
    "category": "Leafy greens",
    "cultural": false,
    "description": "Kale is a vegatable that can be harvested through the months of March until November: it becomes sweeter with each frost that it survives. Dinosaur kale, also known as Tuscany black kale and Italian kale, has long, dark green leaves with a bumpy surface. Though kale has only recently become a popular produce item in the United States, it has long been a staple of East African cooking.",
    "varieties": [],
    "images": [
      "/images/wf/crops/kale-1.jpg"
    ],
    "recipeCount": 3
  },
  {
    "id": "lalu",
    "name": "Lalu",
    "aka": null,
    "scientificName": "Corchorus Olitorius",
    "origin": "Africa",
    "region": "Africa",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Lalu, also known as Molokheiya, Egiptian Spinach, Jews Mallow or Jute plant is  a leafy green popular in Egypt, Middle East, West African, India, and Southeast Asia. This crop is considered an Egypt national dish. With high levels of vitamin C, dietary fiber, and iron, Lalu has become a staple crop in many cuisines.",
    "varieties": [],
    "images": [
      "/images/wf/crops/lalu-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "leeks",
    "name": "Leeks",
    "aka": null,
    "scientificName": "Allium ampeloprasum",
    "origin": "Central Asia",
    "region": "Asia",
    "category": "Roots & alliums",
    "cultural": false,
    "description": "Leeks are a relative of onions, chives, and garlic. The wide leaf bases and slightly developed buds are eaten as a cooked vegetable, in soups, or raw. Leeks have a mild, sweet, delicate taste that is distinct from other members of the onion family. Because they are less intense than other onions, they are a popular substitute for onions when cooking for the elderly, the young, or those with sensitive tastebuds.",
    "varieties": [],
    "images": [
      "/images/wf/crops/leeks-1.jpg"
    ],
    "recipeCount": 3
  },
  {
    "id": "lemongrass",
    "name": "Lemongrass",
    "aka": null,
    "scientificName": "Cymbopogon citratus",
    "origin": "Asia, Africa, Australia",
    "region": "Africa",
    "category": "Herbs",
    "cultural": true,
    "description": "Lemongrass is commonly used as a culinary herb in Asian and Indian cuisines, particularly in curries, soups, and teas. With a subtle citrus flavor, it also pairs well with meat, poultry, and fish dishes. Lemongrass is a natural mosquito repellent; the oil extracted from lemongrass is a key ingredient in all-natural bugsprays and anti-mosquito candles.",
    "varieties": [],
    "images": [
      "/images/wf/crops/lemongrass-1.jpg"
    ],
    "recipeCount": 3
  },
  {
    "id": "lettuce",
    "name": "Lettuce",
    "aka": null,
    "scientificName": "Lactuca sativa",
    "origin": "Asia, Europe, Mediterranean",
    "region": "Asia",
    "category": "Leafy greens",
    "cultural": false,
    "description": "Lettuce is a hardy annual plant of the daisy family. It is most often grown as a leaf vegetable whose succulent leaves are used especially in salads. The nutritional value of lettuce varies with the variety. Lettuce in general provides small amounts of dietary fiber, some carbohydrates, a little protein and a trace of fat. Its most important nutrients are vitamin A and potassium.",
    "varieties": [
      {
        "name": "Lettuce Mix",
        "scientificName": "Lactuca sativa",
        "origin": "Mediterranean",
        "description": "Lettuce mix combines delicate head lettuce leaves with crunchy stem lettuce. Types of lettuce in traditional lettuce mix include Boston lettuce, garden lettuce, cutting lettuce, romaine lettuce,and celtuce, grown for its tasty stalk with a celery-like flavor. Mixed lettuce seeds can be sprouted and used in salads or sandwiches."
      },
      {
        "name": "Red Leaf Lettuce",
        "scientificName": "Lactuca sativa",
        "origin": "Asia, Europe",
        "description": "Red leaf lettuce actually refers to a group of lettuces, all with red or purple leaves. Like other lettuces, red leaf lettuce is virtually calorie-free and high in Vitamin A. Due to its high water content, red leaf lettuce is rarely cooked, and it cannot be frozen. It is a beautiful addition to salads."
      },
      {
        "name": "Romaine Lettuce",
        "scientificName": "Lactuca sativa",
        "origin": "Mediterranean",
        "description": "Romaine lettuce grows in tall heads of sturdy leaves with crunchy stalks. Like other dark leafy greens, romaine contains antioxidants that many believe help to prevent the growth of cancers. Due to its high water content, romaine is almost never cooked, and it cannot be frozen."
      }
    ],
    "images": [
      "/images/wf/crops/lettuce-1.jpg",
      "/images/wf/crops/lettuce-2.jpg"
    ],
    "recipeCount": 5
  },
  {
    "id": "malabar-spinach",
    "name": "Malabar Spinach",
    "aka": null,
    "scientificName": "Basella alba",
    "origin": "China",
    "region": "Asia",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Malabar spinach, also known as Chinese spinach, Indian spinach, or Vine spinach, belongs to the basellaceae family. It originated in Asia and has two main species: Basella alba (green stem) and Basella rubra (red stem). Chinese spinach has a mild, slightly peppery flavor which resembles spinach, yet it has a slimy texture like that of okra. Chinese spinach can be cooked like spinach or eaten raw.",
    "varieties": [],
    "images": [
      "/images/wf/crops/malabar-spinach-1.jpg",
      "/images/wf/crops/malabar-spinach-2.jpg",
      "/images/wf/crops/malabar-spinach-3.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "managu-night-shade",
    "name": "Managu",
    "aka": [
      "Night Shade"
    ],
    "scientificName": "Solanum villosum",
    "origin": "Central Africa",
    "region": "Africa",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Managu is a leafy green vegetable common throughout Kenya and nearby countries. The leaves are usually cooked with other vegetables. Although originally quite bitter, the flavor can be improved by letting it rest several days after cooking or by adding fermented milk. Managu is a crop that grows well in a variety of soil types. It is also referred to as African nightshade, although it is entirely different from the poisonous European solanum nigrum and other poisonous nightshade varieties from different regions of the world.",
    "varieties": [],
    "images": [
      "/images/wf/crops/managu-night-shade-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "maxixe",
    "name": "Maxixe",
    "aka": null,
    "scientificName": "Cucumis sativus",
    "origin": "Africa",
    "region": "Africa",
    "category": "Fruiting vegetables",
    "cultural": true,
    "description": "Maxixe is very similar to cucumber, although it has a distinctive spiny skin. Like cucumber, maxixe can be consumed raw, or pickled. In northeastern Brazil, where maxixe is extremely popular, it may also be boiled, stewed, or deep-fried.",
    "varieties": [],
    "images": [
      "/images/wf/crops/maxixe-1.jpg",
      "/images/wf/crops/maxixe-2.jpg",
      "/images/wf/crops/maxixe-3.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "mint",
    "name": "Mint",
    "aka": null,
    "scientificName": "Mentha",
    "origin": "Mediterranean",
    "region": "Europe & Mediterranean",
    "category": "Herbs",
    "cultural": false,
    "description": "Mint is an herb that comes in many varieties including spearmint, peppermint, and chocolate mint. Spearmint is the variety generally used in cooking, while peppermint and chocolate mint are generally preferred for baking. Fresh mint leaves can be steeped in hot water to make a delicious detoxifying tea. Mint is also a popular accompaniment to lamb and an essential addition to many Mediterranean salads and condiments.",
    "varieties": [],
    "images": [
      "/images/wf/crops/mint-1.jpg"
    ],
    "recipeCount": 3
  },
  {
    "id": "okra",
    "name": "Okra",
    "aka": null,
    "scientificName": "Abelmoschus esculentus",
    "origin": "Asia, Africa",
    "region": "Africa",
    "category": "Fruiting vegetables",
    "cultural": false,
    "description": "Okra has large, deep-green leaves that can withstand hot, dry conditions. The leaves, flowers, and flower buds can be cooked as greens, though by far the most familiar part of the okra plant is the seed pod. The seeds are suspended in a mucilagenous substance that is used as a thickener in many central and West African cuisines. In the United States, okra is most commonly used in gumbo or deep-fried. In Asia, pickled okra is very popular snack.",
    "varieties": [],
    "images": [
      "/images/wf/crops/okra-1.jpg",
      "/images/wf/crops/okra-2.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "onions",
    "name": "Onions",
    "aka": null,
    "scientificName": "Allium cepa",
    "origin": "Central Asia",
    "region": "Asia",
    "category": "Roots & alliums",
    "cultural": false,
    "description": "Onions are used in most countries of the world; in India, onions are such a staple that the price of onions is used as a proxy for nationwide economic health. Onion flowers, which are perhaps the least familiar part of the onion plant, can be used as edible garnishes. Many folk medicine traditions cite onion juice as a treatment of bee and wasp stings.",
    "varieties": [],
    "images": [
      "/images/wf/crops/onions-1.jpg"
    ],
    "recipeCount": 3
  },
  {
    "id": "pak-choi",
    "name": "Pak-Choi",
    "aka": null,
    "scientificName": "Brassica chinensis L.",
    "origin": "China",
    "region": "Asia",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Pak-Choi has Chinese origins. It spread throughout Asia and beyond and developed a wide range of varieties. Pak-Choi can be green, red, or purple. The leaves can be eaten from seedling to mature plant. The slight mustard flavor of pak-choi makes it a delightful addition to stir-fries, soups, noodles, meat dishes, and salads. The immature flowering stems have a sweet flavor and can be cooked like broccoli.",
    "varieties": [
      {
        "name": "Pak-Choi (Red)",
        "scientificName": "Brassica chinensis L.",
        "origin": "China",
        "description": ""
      },
      {
        "name": "Pak-Choi (Purple)",
        "scientificName": "Brassica chinensis L.",
        "origin": "China",
        "description": ""
      }
    ],
    "images": [
      "/images/wf/crops/pak-choi-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "parsley",
    "name": "Parsley",
    "aka": null,
    "scientificName": "Petroselinum crispum",
    "origin": "Europe",
    "region": "Europe & Mediterranean",
    "category": "Herbs",
    "cultural": false,
    "description": "Parsley is an herb that is a good source of Vitamins A, B, and C C. It is often used as an ingredient to flavor a dish that has already been cooked. In Mediterranean cooking, parsley plays a prominent role, and is the principal ingredient in the beloved tabbouleh salad.",
    "varieties": [],
    "images": [
      "/images/wf/crops/parsley-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "pea-tendrils",
    "name": "Pea Tendrils",
    "aka": null,
    "scientificName": "Pisum sativum var. macrocarpon",
    "origin": "Central Asia",
    "region": "Asia",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Pea tendrils, also known as pea shoots, are the young leaves, flowers, stems, and vines of a pea plant. Pea tendrils are harvested before the pea pods are matured; as such, pea tendrils are available in the spring and early summer. They have a similar flavor to peas and can be eaten raw in salads or lightly stir-fried and added to pasta and rice dishes.",
    "varieties": [],
    "images": [
      "/images/wf/crops/pea-tendrils-1.jpg",
      "/images/wf/crops/pea-tendrils-2.jpg"
    ],
    "recipeCount": 1
  },
  {
    "id": "peppers-banana",
    "name": "Peppers (Banana)",
    "aka": null,
    "scientificName": "Capsicum annuum",
    "origin": "South America",
    "region": "Americas",
    "category": "Fruiting vegetables",
    "cultural": false,
    "description": "Banana peppers have a mild, tangy taste; they are much less hot than other members of the chili pepper family. Pickled banana peppers are a popular topping on pizzas and sandwiches. They are a good source of dietary fiber and vitamin A.",
    "varieties": [],
    "images": [
      "/images/wf/crops/peppers-banana-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "peppers-bell",
    "name": "Peppers (Bell)",
    "aka": null,
    "scientificName": "Capsicum annuum",
    "origin": "South America",
    "region": "Americas",
    "category": "Fruiting vegetables",
    "cultural": false,
    "description": "Technically speaking, bell peppers are fruits, though they are treated as vegetables in cooking. Bell peppers come in many colors, from green to red, orange, yellow, and occasionally purple. All are delicious fresh but can also be prepared in numerous other ways. Bell peppers are high in vitamin C, and red peppers are a particularly good source of lycopene.",
    "varieties": [],
    "images": [
      "/images/wf/crops/peppers-bell-1.jpg",
      "/images/wf/crops/peppers-bell-2.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "peppers-biquinho",
    "name": "Peppers (Biquinho)",
    "aka": null,
    "scientificName": "Capsicum chinense",
    "origin": "South America",
    "region": "Americas",
    "category": "Fruiting vegetables",
    "cultural": false,
    "description": "The Biquinho chilli pepper also known as the beak pepper (biquinho translates to me \"little beak\") which is derived from the pepper’s similarity in appearance to a small pointed bird’s beak. It has an antioxidant effect, prevents and treats inflammation, stimulates local circulation and reduces muscle pain. The peppers also contain mild heat that fluctuates in intensity depending on the climate, soil, and environment.",
    "varieties": [],
    "images": [
      "/images/wf/crops/peppers-bell-1.jpg"
    ],
    "recipeCount": 2,
    "imagePlaceholder": true
  },
  {
    "id": "potatoes",
    "name": "Potatoes",
    "aka": null,
    "scientificName": "Solanum tuberosum",
    "origin": "South America",
    "region": "Americas",
    "category": "Roots & alliums",
    "cultural": false,
    "description": "Potatoes are a nutrient-packed, easy to grow crop in the nightshade family. They produce one of the highest yields per acre of any crop and can be grown in any range of soil types because they do not require soil with high nutrition. Potatoes are a good source of vitamin C and carbohydrates. There are many diverse ways to use potatoes; they can be roasted, baked, steamed, boiled, pureed, added to stews, soups, and savory pies, or used to distill alcohol. Before preparing potaoes, it is important to make sure that the tubers do not have green patches, for green parts that have been exposed to light contain solanine, which can be poisonous.",
    "varieties": [],
    "images": [
      "/images/wf/crops/potatoes-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "pumpkins-blossoms-leaves-vines",
    "name": "Pumpkins (Blossoms, Leaves & Vines)",
    "aka": null,
    "scientificName": "Cucurbita",
    "origin": "Mexico",
    "region": "Americas",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Pumpkins are a highly adaptable crop whose unique flavor profile makes it a popular addition to both savory and sweet dishes. Its seeds can be roasted and eaten as a snack or ground into a useful gluten-free powder. The skin can be dried to make containers. The leaves and vines can be cooked like other hearty greens.",
    "varieties": [
      {
        "name": "Pumpkin Blossoms",
        "scientificName": "Cucurbita",
        "origin": "Mexico",
        "description": "Pumpkin flowers are a culinary treat found occasionally mixed in salads, soups, sautés, or cheese-stuffed and batter-fried. They have a lovely sweet mild pumpkin flavor. If you luck enough to have a garden, pick them early in the morning when they are in full bloom, leaving the female to grow into a pumpkin. Pumpkins have separate male and female flowers, with the male flowers appearing first. One or two weeks after they appear, female flowers form and begin to open. Male flowers are borne straight off the vine while females have a small fruit swelling at the base near the stem. You may have the opportunity to buy a small bundle at a farmer’s market. If you do, enjoy the opportunity but plan to cook your pumpkin blossom that day. Unfortunately they do not last long in your refrigerator."
      },
      {
        "name": "Pumpkin Leaves",
        "scientificName": "Cucurbita",
        "origin": "Mexico",
        "description": "Pumpkin leaves, sometimes called pumpkin greens, are edible, nutritious, and delicious. Moreover, using them in your cooking will help you get the most out of your pumpkin plants. Pumpkin greens, like many other leafy vegetables, are teeming with nutritional benefits. In West Africa and India, pumpkin leaves are often used in dishes like soups, stews, and curries. They would also be fantastic even just sautéed with a little olive oil, garlic, tomatoes, and spices."
      },
      {
        "name": "Pumpkin Vines",
        "scientificName": "Cucurbita",
        "origin": "Mexico",
        "description": "The growing tips of pumpkin vines are excellent in a stir fry. I even toss them into soup, quiche, anything really that needs greens. Taking the tips of the vines is a great way to stop the vine taking over your garden."
      }
    ],
    "images": [
      "/images/wf/farm-3777.jpg"
    ],
    "recipeCount": 4,
    "imagePlaceholder": true
  },
  {
    "id": "purslane",
    "name": "Purslane",
    "aka": null,
    "scientificName": "Portulaca oleracea",
    "origin": "Mexico",
    "region": "Americas",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Purslane, also known as little hogweed belongs to the portulaca oleracea family. It is a succulent leafy green used in many Mediterranean and Latino cuisines. It has two cultivated varieties, green leaf and gold, but the wild purslane is also edible. This crop grows well in warm weather and is susceptibility to frost injury; in Australia the plants grow quite large and can produce thousands of seeds, yet to Britain numbers are very low. The leaves are a great source of omega-3 fatty acids. When young, the leaves pair well with salad and can be used as a soup thickener, but as they become older the leaves begin to have a spicy, salty and somewhat sour flavor. The seeds can be group up into a powder then used in that form. Purslane can also be applied to burns, used to treat coughs, insect stings, stomach aches, and much more.",
    "varieties": [],
    "images": [
      "/images/wf/crops/purslane-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "radish",
    "name": "Radish",
    "aka": null,
    "scientificName": "Raphanus sativus",
    "origin": "Europe",
    "region": "Europe & Mediterranean",
    "category": "Roots & alliums",
    "cultural": false,
    "description": "Radish is a crop that grows faster than most. They can grown in as little as 25 days and are one of the earliest crops to be planted. There are different varieties depending on the season they are grown. Cooler temperatures produce milder flavors in radishes while periods of heat will develop a spicy strong flavor. Radish also has benefits while growing: its spicy taste repels pests from tomatoes, cucumbers, and carrots.",
    "varieties": [],
    "images": [
      "/images/wf/crops/radish-1.jpg"
    ],
    "recipeCount": 3
  },
  {
    "id": "scallions",
    "name": "Scallions",
    "aka": null,
    "scientificName": "Allium fistulosum",
    "origin": "Asia",
    "region": "Asia",
    "category": "Herbs",
    "cultural": false,
    "description": "Scallions are related to garlic, leeks, and chives: like its cousins, scallions have an oniony flavor, but are defined by their lack of a round bulb. The white and green parts of the scallion are edible, and are often used as a garnish atop dishes like fried rice and soups.",
    "varieties": [],
    "images": [
      "/images/wf/crops/scallions-1.jpg",
      "/images/wf/crops/scallions-2.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "shanghai-bok-choy",
    "name": "Shanghai Bok-Choy",
    "aka": null,
    "scientificName": "Brassica rapa var. chinensis",
    "origin": "China",
    "region": "Asia",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Shanghai Bok Choy is a variety of bok choy that is tender and finely flavored. It has pale yellow-green stalks with light-green leaves. It is similar in flavor to traditional bok choy.",
    "varieties": [],
    "images": [
      "/images/wf/crops/shanghai-bok-choy-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "shiso-red-red-perilla",
    "name": "Shiso Red",
    "aka": [
      "Red Perilla"
    ],
    "scientificName": "Perilla frutescens var. crispa",
    "origin": "Mexico",
    "region": "Americas",
    "category": "Herbs",
    "cultural": true,
    "description": "Shiso, or perilla, is crop that is rich in calcium and iron. It can grow to be either red or green. The green variety tastes faintly of cinnamon with a hint of coriander or citrus, while the red has an anise flavor. Shiso can be used for cooking, seasoning, garnishing, or pickling. The Japanese use red perilla in coloring umeboshi, pickled ginger, and to wrap around pieces of meat. Green perilla is most commonly served with sushi or sashimi.",
    "varieties": [],
    "images": [
      "/images/wf/crops/shiso-red-red-perilla-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "sorghum",
    "name": "Sorghum",
    "aka": null,
    "scientificName": "Sorghum bicolor",
    "origin": "Australia, Africa",
    "region": "Africa",
    "category": "Grains & staples",
    "cultural": true,
    "description": "Sorghum is a cereal grain that grows in tall stalks, like corn. A drought-resistant crop, it is popular in hot, dry regions. Sorghum is often used as a sweetener, but sorghum seeds can also be used as a whole grain, like rice, ground into flour, or used to distill ethanol. Sorghum stems can be used to weave fences, mats, and baskets; the flowering panicles can be used as brooms and brushes.",
    "varieties": [],
    "images": [
      "/images/wf/crops/sorghum-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "sorrel",
    "name": "Sorrel",
    "aka": null,
    "scientificName": "Rumex acetosa",
    "origin": "Europe",
    "region": "Europe & Mediterranean",
    "category": "Leafy greens",
    "cultural": false,
    "description": "Sorrel has a tangy, citrusy taste. Cold sorrel soup is a popular summer dish in Eastern Europe and Russia, where it is served with a dollop of sour cream to balance the soup's tart flavors. In Nigeria, sorrel is frequently stewed together with spinach and tomatoes. Sorrel is high in vitamin C and potassium",
    "varieties": [],
    "images": [
      "/images/wf/crops/sorrel-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "spinach",
    "name": "Spinach",
    "aka": null,
    "scientificName": "Spinacia oleracea",
    "origin": "Persia",
    "region": "Asia",
    "category": "Leafy greens",
    "cultural": false,
    "description": "Spinach widely varies in size and appearance, depending on the specific variety and maturity of the greens. Spinach leaves are generally dark green to vibrant green, sometimes blushed with red veins or accents, and have a smooth, crinkled texture. Spinach is a source of fiber to regulate the digestive tract, vitamin C to strengthen the immune system while reducing inflammation, and potassium to balance fluid levels within the body. The greens are generally incorporated into salads, as a bed of greens, or stirred into dips, spreads, and sauces such as pesto.",
    "varieties": [],
    "images": [
      "/images/wf/crops/spinach-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "squash",
    "name": "Squash",
    "aka": null,
    "scientificName": "Cucurbita",
    "origin": "Middle East, West Asia",
    "region": "Asia",
    "category": "Squash & gourds",
    "cultural": false,
    "description": "Squash varieties include zucchini, patty pan, pumpkins, and butternuts. Around the world, squashes are used in countless ways, including curries, soups, stir-fries, and sweet dishes like puddings, cakes, and pies. The seeds are rich in oil with a pleasant nutty flavor. The leaves, young stems, and blossoms are all edible.",
    "varieties": [
      {
        "name": "Squash Blossom",
        "scientificName": "Cucurbita pepo",
        "origin": "Middle East / West Asia",
        "description": "Squash blossoms are also sometimes called zucchini flowers, but they can come from any summer or even winter squashes. They are the edible flowers of the squash plant and usually come in yellow and orange shades. Squash blossoms are soft, delicate, and taste mildly like the squash itself. They are available late spring to early fall."
      },
      {
        "name": "Squash (Blue Hubbard)",
        "scientificName": "Cucurbita maxima",
        "origin": "Japan",
        "description": "Blue hubbard squash has a bumpy, grey-blue skin, but is sweet on the inside. They are large in size so they are often sold in precut wedges. Blue Hubbard squashes can be prepared in the same ways as pumpkin, though they tend to be slightly less sweet. Squash (Brazilian) Despite its name, Brazilian squash was actually a hybrid developed in Japan that migrated to Brazil. Its texture and flavor is comparable to butternut squash, although its not as sweet."
      },
      {
        "name": "Squash Patty Pan (Sunburst Squash)",
        "scientificName": "Cucurbita pepo L.",
        "origin": "Central America",
        "description": "Patty pan squash are small, 2-3 inches in diameter, with scalloped edges and a flattened, disc-like shape. Patty pan squash are very mild, with a buttery texture and clean, squash flavor. Patty pan grown in a number of different sizes and colors from white to yellow, to orange or green. Are 90% water, and are a good source of vitamin A, magnesium, copper and iron. Select firm, shiny squash, free from cuts and blemishes."
      }
    ],
    "images": [
      "/images/wf/crops/squash-1.jpg",
      "/images/wf/crops/squash-2.jpg",
      "/images/wf/crops/squash-3.jpg"
    ],
    "recipeCount": 5
  },
  {
    "id": "sugar-snap-peas",
    "name": "Sugar Snap Peas",
    "aka": null,
    "scientificName": "Pisum sativum var. macrocarpon",
    "origin": "Central Asia",
    "region": "Asia",
    "category": "Beans & peas",
    "cultural": false,
    "description": "Sugar snap peas are a nutritious, crunchy, and flavorful vegetable. They can be eaten whole or separately from the pods. The pods are similar to those of English peas, but are much sweeter. Immature seed pods are high in protein. Leaves and young shoots can be cooked or eaten raw. Mature seeds can be dried, ground into powder, sprouted and added into a salad, or rehydrated and cooked.",
    "varieties": [],
    "images": [
      "/images/wf/crops/sugar-snap-peas-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "sweet-potato-leaves",
    "name": "Sweet Potato Leaves",
    "aka": null,
    "scientificName": "Ipomoea batatas",
    "origin": "South America",
    "region": "Americas",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Sweet potato leaves, greens, or vines (Ipomoea batatas) are similar to spinach but have less oxalic acid, giving sweet potato leaves a less sharp taste. In the US, the sweet potato is grown predominantly for its fiber-rich tuber, but across continents such as Asia and Africa, and in the Pacific Islands, it is the leaves which are primarily consumed. They can be prepared much in the same fashion as you would spinach or collard greens. While white potato leaves are toxic - they come from the nightshade family - sweet potato leaves - from the morning glory family - are much sought after for their flavor and their health benefits. Eating sweet potato leaves is said to improve immune functions and reduce cardiovascular disease risk, and some studies have suggested that the high antioxidant level in the peel of the sweet potato may reduce the risk of cancer. This crop is rich in vitamins A, C, B, β-carotene, iron, calcium, zinc, and protein.",
    "varieties": [],
    "images": [
      "/images/wf/crops/sweet-potato-leaves-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "swiss-chard",
    "name": "Swiss Chard",
    "aka": null,
    "scientificName": "Beta vulgaris L. subsp. cicla",
    "origin": "Mediterranean",
    "region": "Europe & Mediterranean",
    "category": "Leafy greens",
    "cultural": false,
    "description": "Swiss chard is a plant from Greece and is in the same family as beets. The stems grown in a variety of colors, from red to orange, yellow, and white. The young leaves are best for fresh eating and the larger leaves and stalks are best for cooking. They are a very good substitute for spinach or broccoli. The tender leaves can be used for salads, sandwiches, sautés and soups, while the fleshy leaf ribscan be prepared much like celery or asparagus.",
    "varieties": [],
    "images": [
      "/images/wf/crops/swiss-chard-1.jpg",
      "/images/wf/crops/swiss-chard-2.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "taioba",
    "name": "Taioba",
    "aka": null,
    "scientificName": "Xanthosoma sagittifolium",
    "origin": "South America",
    "region": "Americas",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Taioba, also known as arrowleaf elephant ear, is native to South America. It is a popular crop in Southeastern Brazil and is also a staple in parts of Africa; in Ghana, it is called nkontomire. The large green leaves can be boiled or sauteed, sometimes with coconut milk. The stems can also be cooked.",
    "varieties": [],
    "images": [
      "/images/wf/crops/taioba-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "tareh",
    "name": "Tareh",
    "aka": null,
    "scientificName": "Allium ampeloprasum subsp. persicum",
    "origin": "Middle East",
    "region": "Europe & Mediterranean",
    "category": "Herbs",
    "cultural": true,
    "description": "Tareh also known as broad leaf leek is native to the Middle East and Iran and is one of the most widely used herbal foods among Iranians. I is a member of the onion family but unlike onion or garlic, Tereh does not develop real bulbs or produce cloves. It is flatter and wider than chives and only the stems and leaves are used. Tareh is served fresh as part of sabzi khordan and chopped for stews and egg dishes and have a flavor similar to leeks.",
    "varieties": [],
    "images": [
      "/images/wf/crops/tareh-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "tat-soi",
    "name": "Tat Soi",
    "aka": null,
    "scientificName": "Brassica narinosa",
    "origin": "Asia",
    "region": "Asia",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Tat Soi has tougher leaves than Pak choi and also has a stronger flavor. The nutrition level and flavor intensity is dependent on the color: the darker the leaves, the higher the nutrition and flavor. The leaves and immature flowering plants can be eaten raw or sauteed, braised, or added to soups.",
    "varieties": [],
    "images": [
      "/images/wf/crops/tat-soi-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "tetragon-spinach",
    "name": "Tetragon Spinach",
    "aka": null,
    "scientificName": "Tetragonia tetragonioides",
    "origin": "South America, China, Australia",
    "region": "Asia",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Tetragon spinach, also known as New Zealand spinach, is an excellent warm-season crop. It is not a true spinach, but it can tolerate heat and drought much better than true spinach. It can grow in cool, damp conditions, but will not survive the first frost. Tetragon spinach has succulent leaves and branch tips that can be used in place of spinach, both in salads and cooked.",
    "varieties": [],
    "images": [
      "/images/wf/crops/tetragon-spinach-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "tomatillos",
    "name": "Tomatillos",
    "aka": null,
    "scientificName": "Physalis philadelphica",
    "origin": "Mesoamerica",
    "region": "Americas",
    "category": "Fruiting vegetables",
    "cultural": true,
    "description": "Tomatillos belong to the nightshade family and come in two varieties, green and purple. Purple tomatillos are the most distinctive. Tomatillos are native to Mesoamerica and are used in many Mexican and Central Ameican dishes. The primary use for tomatillos are green sauces, like the iconic salse verde. Tomatillos are a delicious flavoring when used in place of tomatoes in soups or stews. The fruit grows wrapped in a protective \"bag\" called a calyx, which should not be eaten because of its toxicity.",
    "varieties": [],
    "images": [
      "/images/wf/crops/tomatillos-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "tomatoes",
    "name": "Tomatoes",
    "aka": null,
    "scientificName": "Solanum lycopersicum",
    "origin": "South America",
    "region": "Americas",
    "category": "Fruiting vegetables",
    "cultural": false,
    "description": "Tomatoes are an important crop throughout the world, although they are a relatively recent arrival to Europe, Africa, and Asia. Nonetheless, tomatoes have been enthusastically adopted by many cuisines and are now considered a staple of Italian, Indian, and Spanish cooking, among others. Tomatoes come in many shapes and sizes, from the bite-sized cherry tomato to hefty heirloom varieties: the largest tomato on record weighed over 8 pounds!",
    "varieties": [],
    "images": [
      "/images/wf/crops/tomatoes-1.jpg",
      "/images/wf/crops/tomatoes-2.jpg",
      "/images/wf/crops/tomatoes-3.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "turnips",
    "name": "Turnips",
    "aka": null,
    "scientificName": "Brassica rapa subsp. rapa",
    "origin": "South America",
    "region": "Americas",
    "category": "Roots & alliums",
    "cultural": false,
    "description": "Turnips boast a mild, crisp, tender flesh with a surprisingly delicate flavor. They are often prepared much like radishes: pickled, steamed, added to stir-fry, or grated raw into salad. Turnip greens are also edible, though mature greens can have an intense bitter taste. Turnips are high in vitamin C.",
    "varieties": [],
    "images": [
      "/images/wf/crops/turnips-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "water-spinach",
    "name": "Water Spinach",
    "aka": null,
    "scientificName": "Ipomoea aquatica",
    "origin": "Central & South Asia",
    "region": "Asia",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Water spinach is a semi-aquatic perennial plant. It is a beloved green for many Southeast Asians, who may prepare it by sauteeing in very hot oil with some garlic and a splash of fish sauce. In the United States, water spinach is a federally-controlled invasive species; special permitting systems were established by UMass specifically to allow Vietnamese immigrants in Massachusetts to grow it.",
    "varieties": [],
    "images": [
      "/images/wf/crops/water-spinach-1.jpg",
      "/images/wf/crops/water-spinach-2.jpg"
    ],
    "recipeCount": 1
  },
  {
    "id": "winter-melon",
    "name": "Winter Melon",
    "aka": null,
    "scientificName": "Benincasa hispida",
    "origin": "Egypt, China",
    "region": "Africa",
    "category": "Squash & gourds",
    "cultural": true,
    "description": "Winter melons are large, light green colored gourds that were first grown as a crop in Egypt and is a staple crop in China. The inside is white and has a very mild flavor; for this reason, it is a popular ingredient in soups, where it absorbs the taste of whatever broth it is boiled in. Winter melon soup is a classic dish in Chinese cooking.",
    "varieties": [],
    "images": [
      "/images/wf/crops/winter-melon-1.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "yardlong-bean",
    "name": "Yardlong Bean",
    "aka": null,
    "scientificName": "Vigna unguiculata (L.) Walp. ssp. sesquipedalis (L.) Verdc.",
    "origin": "Souther Asia",
    "region": "Asia",
    "category": "Beans & peas",
    "cultural": true,
    "description": "The Yardlong Bean is reported to be one of the top ten vegetables in Southeast Asia, especially in Taiwan, southern China, and Bangladesh. It is the most widely grown legume of the Philippines where it is known as “poor man’s meat.” Yardlong Bean is grown chiefly for the long immature pods that resemble French green beans in flavor. The raw pods reportedly have a mushroom-like flavor. Some users claim an asparagus flavor for them, as reflected by the name, “Asparagus Bean.” Young leaves and stem tips are eaten steamed as a vegetable.",
    "varieties": [],
    "images": [
      "/images/wf/crops/yardlong-bean-1.jpg"
    ],
    "recipeCount": 1
  },
  {
    "id": "yu-choi",
    "name": "Yu Choi",
    "aka": null,
    "scientificName": "Brassica rapa var. parachinensis",
    "origin": "China",
    "region": "Asia",
    "category": "Leafy greens",
    "cultural": true,
    "description": "Yu Choi is now grown mainly for its young leaves and flowering stalks; historically, however, it was grown for its seeds, from which cooking and lamp oil was extracted. Yu Choi is similar to Chinese Broccoli, with a cabbage-like flavor and a flowering stem. It is popular in stir-fries or simply steamed and tossed with soy sauce or oyster sauce.",
    "varieties": [],
    "images": [
      "/images/wf/crops/yu-choi-1.jpg",
      "/images/wf/crops/yu-choi-2.jpg"
    ],
    "recipeCount": 2
  },
  {
    "id": "zucchini",
    "name": "Zucchini",
    "aka": null,
    "scientificName": "Cucurbita pepo var. cylindrica",
    "origin": "Americas",
    "region": "Americas",
    "category": "Squash & gourds",
    "cultural": false,
    "description": "Zucchini are a common garden vegetable, thanks to their ease of growth. Like all squash, zucchini have their roots in the Americas, but the common green zucchini is actually a variety that was developed in northern Italy some generations after the introduction of cucurbits from North America. Zucchini flowers are edible and can be added fresh to salads or stuffed and lightly fried.",
    "varieties": [],
    "images": [
      "/images/wf/crops/zucchini-1.jpg"
    ],
    "recipeCount": 2
  }
];
